import Phaser from 'phaser';
import {
  loadScore, elapsedMs, formatElapsed,
  markFinishedIfDone, resetScore, winCount,
} from '../state/scoreboard';
import { INVENTIONS } from '../data/inventions';
import { BIG_THINGS } from '../data/bigThings';
import { resetUnlocked, unlockedIds } from '../state/milestones';

/**
 * End-of-game screen. Fires when all 24 cards have been used (won or
 * revealed). Trophy is the hero — every player who reaches the end gets
 * one. The monument gallery sits below as a quiet record of what was
 * earned vs missed.
 */
export class CeremonyScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Ceremony', active: false });
  }

  create() {
    markFinishedIfDone(INVENTIONS.length);
    const score = loadScore();
    const elapsed = elapsedMs(score);
    const wins = winCount(score);
    const monumentsEarned = unlockedIds().size;

    const W = this.game.config.width as number;
    const H = this.game.config.height as number;
    const narrow = W < 1100;

    const continent = this.scene.get('Continent') as Phaser.Scene & {
      setChromeVisible?: (v: boolean) => void;
    };
    continent?.setChromeVisible?.(false);
    this.events.once('shutdown', () => continent?.setChromeVisible?.(true));

    this.add.rectangle(W / 2, H / 2, W, H, 0x0a0806, 0.96).setInteractive();

    // ---- Trophy hero (anyone who reaches the end gets one) ----
    const trophyKey = 'trophy';
    const trophyTex = this.textures.exists(trophyKey)
      && this.textures.get(trophyKey).source[0]?.width > 1
      ? this.textures.get(trophyKey)
      : null;
    const trophyTargetH = narrow ? 360 : 280;
    const trophyY = narrow ? 360 : 290;
    if (trophyTex) {
      const nativeH = trophyTex.source[0].height;
      const trophy = this.add.image(W / 2, trophyY, trophyKey)
        .setOrigin(0.5)
        .setScale(trophyTargetH / nativeH);
      trophy.setAlpha(0);
      this.tweens.add({
        targets: trophy,
        alpha: 1,
        scale: { from: (trophyTargetH / nativeH) * 0.7, to: trophyTargetH / nativeH },
        duration: 800, ease: 'Back.Out',
      });
    }

    // ---- Punchy headline below the trophy ----
    const headlineY = trophyY + trophyTargetH / 2 + (narrow ? 60 : 50);
    this.add.text(W / 2, headlineY, this.headlineFor(monumentsEarned), {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '64px' : '52px',
      color: '#ffd966', fontStyle: 'italic',
      align: 'center',
      wordWrap: { width: W - 60 },
    }).setOrigin(0.5);

    // ---- Quiet stats line: just one number that matters ----
    const statsY = headlineY + (narrow ? 70 : 56);
    this.add.text(W / 2, statsY,
      `${wins}/${INVENTIONS.length} solved   ·   ${formatElapsed(elapsed)}`,
      {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: narrow ? '24px' : '18px',
        color: '#b19b6e',
      }).setOrigin(0.5);

    // ---- Small monument gallery: trophies you earned (no labels) ----
    const galleryY = statsY + (narrow ? 90 : 70);
    const galleryIconH = narrow ? 70 : 56;
    const slotW = (W - 80) / BIG_THINGS.length;
    const unlocked = unlockedIds();
    BIG_THINGS.forEach((bt, i) => {
      const cx = 40 + slotW * (i + 0.5);
      const key = `bigthing-${bt.id}`;
      if (this.textures.exists(key) && this.textures.get(key).source[0]?.width > 1) {
        const tex = this.textures.get(key);
        const nativeH = tex.source[0].height;
        const img = this.add.image(cx, galleryY, key)
          .setOrigin(0.5)
          .setScale(galleryIconH / nativeH);
        const isEarned = unlocked.has(bt.id);
        if (!isEarned) img.setTint(0x1a0e06);
        img.setAlpha(0);
        this.tweens.add({
          targets: img, alpha: isEarned ? 1 : 0.4,
          duration: 400, delay: 600 + i * 100, ease: 'Sine.Out',
        });

        // Red X overlay for missed monuments. Uses the baked PixelLab
        // red X if available, else falls back to a chunky text X.
        if (!isEarned) {
          const xKey = 'red-x';
          if (this.textures.exists(xKey) && this.textures.get(xKey).source[0]?.width > 1) {
            const xTex = this.textures.get(xKey);
            const xNativeH = xTex.source[0].height;
            const xScale = (galleryIconH * 0.95) / xNativeH;
            const x = this.add.image(cx, galleryY, xKey)
              .setOrigin(0.5)
              .setScale(xScale)
              .setDepth(1);
            x.setAlpha(0);
            this.tweens.add({
              targets: x, alpha: 1,
              duration: 300, delay: 800 + i * 100, ease: 'Sine.Out',
            });
          } else {
            const x = this.add.text(cx, galleryY, '✗', {
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: `${Math.round(galleryIconH)}px`,
              color: '#c8202a', fontStyle: 'bold',
            }).setOrigin(0.5).setDepth(1);
            x.setAlpha(0);
            this.tweens.add({
              targets: x, alpha: 1,
              duration: 300, delay: 800 + i * 100, ease: 'Sine.Out',
            });
          }
        }
      }
    });

    // ---- Confetti ----
    this.spawnConfetti();

    // ---- Primary share button ----
    const btnY = narrow ? H - 130 : H - 90;
    const shareBtn = this.add.text(W / 2, btnY, '▸  share my run  ▸', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '24px' : '18px',
      color: '#0d0a08', backgroundColor: '#ffd966',
      padding: { x: narrow ? 28 : 22, y: narrow ? 14 : 11 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    shareBtn.on('pointerover', () => shareBtn.setBackgroundColor('#fff5c0'));
    shareBtn.on('pointerout', () => shareBtn.setBackgroundColor('#ffd966'));
    shareBtn.on('pointerup', () => this.shareRun(shareBtn, elapsed));

    // ---- Secondary back / reset (small, quiet) ----
    const linkY = btnY + (narrow ? 50 : 38);
    const closeBtn = this.add.text(W / 2 - (narrow ? 90 : 70), linkY, 'back to map', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '18px' : '13px',
      color: '#8a7a5e',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#b6a079'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#8a7a5e'));
    closeBtn.on('pointerup', () => this.scene.stop());

    const replay = this.add.text(W / 2 + (narrow ? 90 : 70), linkY, 'reset & start over', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '18px' : '13px', color: '#5a4e3a',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    replay.on('pointerover', () => replay.setColor('#b19b6e'));
    replay.on('pointerout', () => replay.setColor('#5a4e3a'));
    replay.on('pointerup', () => {
      try {
        localStorage.removeItem('pa-revealed');
        localStorage.removeItem('pa-revealed-status');
        localStorage.removeItem('pa-intro-seen');
      } catch {}
      resetScore();
      resetUnlocked();
      window.location.reload();
    });
  }

  /**
   * Aussie-flavoured rank copy based on monuments earned. Drives the
   * headline emotional verdict.
   */
  private headlineFor(monumentsEarned: number): string {
    if (monumentsEarned >= 5) return 'Bloody legend.';
    if (monumentsEarned >= 4) return 'Howzat!';
    if (monumentsEarned >= 3) return 'Fair dinkum.';
    if (monumentsEarned >= 2) return 'Had a crack.';
    if (monumentsEarned >= 1) return 'Made a start.';
    return 'Yeah, nah.';
  }

  private spawnConfetti() {
    const W = this.game.config.width as number;
    const colours = [0xffd966, 0xfff5c0, 0xa83320, 0x88ff66, 0xffe78a, 0x4a8db8, 0xb87a3a];
    for (let i = 0; i < 80; i++) {
      const x = Phaser.Math.Between(0, W);
      const colour = colours[i % colours.length];
      const r = this.add.rectangle(x, -20, 5, 5, colour);
      r.setDepth(200);
      this.tweens.add({
        targets: r,
        y: this.game.config.height as number + 20,
        x: x + Phaser.Math.Between(-150, 150),
        angle: Phaser.Math.Between(-540, 540),
        alpha: { from: 1, to: 0.4 },
        duration: Phaser.Math.Between(2000, 3500),
        ease: 'Sine.In',
        delay: Phaser.Math.Between(0, 1200),
        onComplete: () => r.destroy(),
      });
    }
  }

  private shareRun(btn: Phaser.GameObjects.Text, elapsed: number) {
    const score = loadScore();
    const wins = winCount(score);
    const monuments = unlockedIds().size;
    // Wordle-style emoji grid: filled slots for earned monuments, empty
    // for missed. Quick visual hook for chat.
    const grid = BIG_THINGS.map((bt) => unlockedIds().has(bt.id) ? '🟩' : '⬛').join('');
    const text = [
      'Auscyclopedia | things built here',
      `${this.headlineFor(monuments)}`,
      grid,
      `${wins}/${INVENTIONS.length} solved · ${formatElapsed(elapsed)}`,
      '',
      'https://auscyclopedia.pages.dev',
    ].join('\n');

    const ok = () => {
      const original = btn.text;
      btn.setText('✓  copied');
      this.time.delayedCall(1400, () => btn.setText(original));
    };
    const nav = navigator as any;
    if (nav.share) {
      nav.share({ title: 'Auscyclopedia', text }).then(ok).catch(() => this.copyText(text, ok));
    } else {
      this.copyText(text, ok);
    }
  }

  private copyText(text: string, ok: () => void) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(ok).catch(() => {});
    }
  }
}
